import { create } from 'zustand'
import type { User } from '../types'
import { queryClient } from '../lib/queryClient'

// ── Types ──────────────────────────────────────────────────────────────────────

export interface AuthState {
  user: User | null
  accessToken: string | null
  refreshToken: string | null
  isAuthenticated: boolean
  login: (user: User, accessToken: string, refreshToken: string) => void
  logout: () => void
  setTokens: (accessToken: string, refreshToken: string) => void
  setUser: (user: User) => void
}

// ── Stable selectors (module-level constants) ──────────────────────────────────
//
// Zustand v5 wraps getSnapshot in useCallback([api, selector]).
// Inline selectors like `(s) => s.user` create a new function reference on
// every render, so useCallback's deps change every render, which gives React 19
// a new getSnapshot function every render.  React 19's updateSyncExternalStore
// then always schedules updateStoreInstance (HasEffect | Passive) because
// `hook.getSnapshot !== getSnapshot`.  Under concurrent-mode flushing this
// causes the infinite loop:  updateStoreInstance → forceStoreRerender → render
// → updateStoreInstance → … → "Maximum update depth exceeded".
//
// Module-level selectors have a stable reference: useCallback deps never change,
// getSnapshot stays the same across renders, updateStoreInstance is only
// scheduled when the state actually changes.  Loop eliminated.
export const selectIsAuthenticated = (s: AuthState) => s.isAuthenticated
export const selectUser = (s: AuthState) => s.user
export const selectAccessToken = (s: AuthState) => s.accessToken
export const selectSetUser = (s: AuthState) => s.setUser

// ── Persistence helpers ────────────────────────────────────────────────────────
//
// The persist middleware was removed because it overrides api.setState and
// api.getInitialState, adding complexity that the manual approach below handles
// more predictably with React 19's stricter useSyncExternalStore checks.

const STORAGE_KEY = 'cardsmith-auth'

// Keys that are user-scoped and must not bleed across a user change.
// Add any new user-scoped key here so the teardown covers it automatically.
const LS_USER_KEYS = [
  'cs_daily_correct',
  'cardsmith-onboarding-progress', // onboarding wizard state — cleared on logout and on completion
] as const
const SS_USER_KEYS = ['cs_active_session', 'cs-demo-lang'] as const

/** Wipe user-scoped storage. Exported for the login-over-session path in client.ts. */
export function clearUserScopedStorage(): void {
  try {
    LS_USER_KEYS.forEach((k) => localStorage.removeItem(k))
    SS_USER_KEYS.forEach((k) => sessionStorage.removeItem(k))
  } catch { /* storage may be unavailable */ }
}

type PersistedSlice = Pick<AuthState, 'user' | 'accessToken' | 'refreshToken' | 'isAuthenticated'>

function loadPersistedAuth(): PersistedSlice {
  const empty: PersistedSlice = {
    user: null,
    accessToken: null,
    refreshToken: null,
    isAuthenticated: false,
  }
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return empty
    const parsed = JSON.parse(raw)
    // Support both formats: { state: {...} } (written below) and legacy root-level.
    const s = parsed?.state ?? parsed
    return {
      user: s?.user ?? null,
      accessToken: s?.accessToken ?? null,
      refreshToken: s?.refreshToken ?? null,
      isAuthenticated: s?.isAuthenticated ?? false,
    }
  } catch {
    return empty
  }
}

function saveAuth(slice: PersistedSlice): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ state: slice }))
  } catch {
    // localStorage may be unavailable in some environments — fail silently.
  }
}

// Read synchronously before store creation so the initial getState() snapshot
// already contains the user's session — no async hydration, no tearing.
const preloaded = loadPersistedAuth()

// ── Store ──────────────────────────────────────────────────────────────────────

export const useAuthStore = create<AuthState>()((set, get) => ({
  ...preloaded,

  login: (user, accessToken, refreshToken) => {
    set({ user, accessToken, refreshToken, isAuthenticated: true })
    saveAuth({ user, accessToken, refreshToken, isAuthenticated: true })
  },

  logout: () => {
    clearUserScopedStorage()
    queryClient.clear()
    set({ user: null, accessToken: null, refreshToken: null, isAuthenticated: false })
    saveAuth({ user: null, accessToken: null, refreshToken: null, isAuthenticated: false })
  },

  setTokens: (accessToken, refreshToken) => {
    set({ accessToken, refreshToken })
    const { user, isAuthenticated } = get()
    saveAuth({ user, accessToken, refreshToken, isAuthenticated })
  },

  setUser: (user) => {
    set({ user })
    const { accessToken, refreshToken, isAuthenticated } = get()
    saveAuth({ user, accessToken, refreshToken, isAuthenticated })
  },
}))
